import React, { useState, useRef } from 'react';
import { db, storage } from '../../../firebase';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { ref as sRef, getDownloadURL, deleteObject } from 'firebase/storage';
import { uploadOptimizedImage } from '../../../services/imageOptimizer';
import { X, UserPlus, Image as ImageIcon, BookOpen, Trash2, Youtube, Award, Plus, Check, Loader2, Edit2, Eye, EyeOff } from 'lucide-react';
import { TeacherItem, TeacherBook, SubjectType, SUBJECT_TYPES } from '../../../models';

interface TeacherFormProps {
    teacher: TeacherItem | null;
    onClose: () => void;
    onSave: () => void;
}

const TeacherForm: React.FC<TeacherFormProps> = ({ teacher, onClose, onSave }) => {
    // Form fields state
    const [name, setName] = useState(teacher?.name || '');
    const [subject, setSubject] = useState<SubjectType>((teacher?.subject as SubjectType) || '국어');
    const [career, setCareer] = useState(teacher?.career || '');
    const [intro, setIntro] = useState(teacher?.intro || '');
    const [youtubeUrl, setYoutubeUrl] = useState(teacher?.youtubeUrl || '');
    const [hideMainPhoto, setHideMainPhoto] = useState(teacher?.hideMainPhoto || false);
    const [hideIntroPhoto, setHideIntroPhoto] = useState(teacher?.hideIntroPhoto || false);
    
    // Photo upload state
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(teacher?.imageUrl || null);
    const [isDraggingProfile, setIsDraggingProfile] = useState(false);

    // Textbook list state
    const [booksList, setBooksList] = useState<TeacherBook[]>(teacher?.booksList || []);

    // Individual textbook uploader & editor state
    const [editingBookIndex, setEditingBookIndex] = useState<number | null>(null);
    const [newBookName, setNewBookName] = useState('');
    const [newBookDesc, setNewBookDesc] = useState('');
    const [newBookImageFile, setNewBookImageFile] = useState<File | null>(null);
    const [newBookImagePreview, setNewBookImagePreview] = useState<string | null>(null);
    const [isDraggingBook, setIsDraggingBook] = useState(false);
    const [uploadingBookFile, setUploadingBookFile] = useState(false);

    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const bookFileInputRef = useRef<HTMLInputElement>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 선택할 수 있습니다.');
            return;
        }
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleBookImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 선택할 수 있습니다.');
            return;
        }
        setNewBookImageFile(file);
        setNewBookImagePreview(URL.createObjectURL(file));
    };

    const handleProfileDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingProfile(true);
    };

    const handleProfileDragLeave = () => {
        setIsDraggingProfile(false);
    };

    const handleProfileDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingProfile(false);
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 드롭할 수 있습니다.');
            return;
        }
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleBookDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingBook(true);
    };

    const handleBookDragLeave = () => {
        setIsDraggingBook(false);
    };

    const handleBookDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingBook(false);
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 드롭할 수 있습니다.');
            return;
        }
        setNewBookImageFile(file);
        setNewBookImagePreview(URL.createObjectURL(file));
    };

    const handleStartEditBook = (index: number) => {
        const book = booksList[index];
        setEditingBookIndex(index);
        setNewBookName(book.name);
        setNewBookDesc(book.desc || '');
        setNewBookImagePreview(book.imageUrl || null);
        setNewBookImageFile(null);
    };

    const handleCancelEditBook = () => {
        setEditingBookIndex(null);
        setNewBookName('');
        setNewBookDesc('');
        setNewBookImagePreview(null);
        setNewBookImageFile(null);
        if (bookFileInputRef.current) bookFileInputRef.current.value = '';
    };

    const handleSaveBook = async () => {
        if (!newBookName.trim()) {
            alert('교재명을 입력해 주세요.');
            return;
        }
        if (editingBookIndex === null && !newBookImageFile) {
            alert('교재 표지 이미지를 선택해 주세요.');
            return;
        }

        setUploadingBookFile(true);
        try {
            let downloadUrl = editingBookIndex !== null ? (booksList[editingBookIndex].imageUrl || '') : '';

            if (newBookImageFile) {
                const fileName = `${Date.now()}_book_${newBookImageFile.name.replace(/\.[^/.]+$/, "")}.webp`;
                const storageRef = sRef(storage, `teachers_books/${fileName}`);
                const snapshot = await uploadOptimizedImage(storageRef, newBookImageFile, { maxWidth: 1000, quality: 0.85 });
                downloadUrl = await getDownloadURL(snapshot.ref);

                if (editingBookIndex !== null && booksList[editingBookIndex].imageUrl) {
                    try {
                        const oldRef = sRef(storage, booksList[editingBookIndex].imageUrl);
                        await deleteObject(oldRef);
                    } catch (err) {
                        console.warn('Failed to delete old book image:', err);
                    }
                }
            }

            if (editingBookIndex !== null) {
                setBooksList(prev => prev.map((b, idx) => {
                    if (idx === editingBookIndex) {
                        return {
                            ...b,
                            name: newBookName.trim(),
                            desc: newBookDesc.trim(),
                            imageUrl: downloadUrl
                        };
                    }
                    return b;
                }));
                setEditingBookIndex(null);
            } else {
                const newBook: TeacherBook = {
                    id: Date.now().toString(),
                    name: newBookName.trim(),
                    desc: newBookDesc.trim(),
                    imageUrl: downloadUrl
                };
                setBooksList(prev => [...prev, newBook]);
            }

            setNewBookName('');
            setNewBookDesc('');
            setNewBookImageFile(null);
            setNewBookImagePreview(null);
            if (bookFileInputRef.current) bookFileInputRef.current.value = '';
        } catch (err) {
            console.error('Error saving book:', err);
            alert('교재 표지 업로드 및 저장에 실패했습니다.');
        } finally {
            setUploadingBookFile(false);
        }
    };

    const handleRemoveBookFromList = async (indexToRemove: number) => {
        const targetBook = booksList[indexToRemove];
        if (!confirm(`교재 "${targetBook.name}"을 목록에서 삭제하시겠습니까?`)) return;

        if (editingBookIndex === indexToRemove) {
            handleCancelEditBook();
        }

        if (targetBook.imageUrl) {
            try {
                const storageRef = sRef(storage, targetBook.imageUrl);
                await deleteObject(storageRef);
            } catch (err) {
                console.warn('Failed to delete book image from storage:', err);
            }
        }

        setBooksList(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            alert('이름을 입력해 주세요.');
            return;
        }

        setSaving(true);
        try {
            let finalImageUrl = teacher?.imageUrl || '';

            // Upload profile image if changed
            if (imageFile) {
                const fileName = `${Date.now()}_profile_${imageFile.name.replace(/\.[^/.]+$/, "")}.webp`;
                const storageRef = sRef(storage, `teachers/${fileName}`);
                const snapshot = await uploadOptimizedImage(storageRef, imageFile, { maxWidth: 1200, quality: 0.85 });
                finalImageUrl = await getDownloadURL(snapshot.ref);

                if (teacher?.imageUrl) {
                    try {
                        const oldRef = sRef(storage, teacher.imageUrl);
                        await deleteObject(oldRef);
                    } catch (err) {
                        console.warn('Failed to delete old profile image:', err);
                    }
                }
            }

            const docData = {
                name: name.trim(),
                subject: subject,
                career: career.trim(),
                intro: intro.trim(),
                imageUrl: finalImageUrl,
                booksList: booksList,
                youtubeUrl: youtubeUrl.trim(),
                hideMainPhoto: hideMainPhoto,
                hideIntroPhoto: hideIntroPhoto
            };

            if (teacher) {
                // Update
                const docRef = doc(db, 'teachers', teacher.id);
                await updateDoc(docRef, docData);
                alert('강사 정보가 수정되었습니다.');
            } else {
                // Add
                if (!finalImageUrl) {
                    alert('프로필 이미지를 선택해 주세요.');
                    setSaving(false);
                    return;
                }
                await addDoc(collection(db, 'teachers'), {
                    ...docData,
                    createdAt: Date.now()
                });
                alert('강사가 새로 등록되었습니다.');
            }

            onSave();
        } catch (error) {
            console.error('Error saving teacher:', error);
            alert('정보 저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-8 py-5 flex items-center justify-between shrink-0 shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-650">
                        <UserPlus size={20} />
                    </div>
                    <div className="text-left">
                        <h3 className="text-lg font-black text-slate-900">
                            {teacher ? '강사 정보 수정' : '새 강사 프로필 등록'}
                        </h3>
                        <p className="text-xs text-slate-450 font-semibold mt-0.5">
                            강사의 기본 정보와 대표 교재 라인업을 한눈에 편집합니다.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-slate-400 hover:text-slate-650 hover:bg-slate-100 transition-all p-2 rounded-xl cursor-pointer"
                    disabled={saving}
                >
                    <X size={20} />
                </button>
            </div>

            {/* Split Panel Body */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 min-h-0 overflow-hidden">
                {/* Left Panel: Basic Info */}
                <div className="lg:col-span-5 p-8 overflow-y-auto space-y-6 bg-white flex flex-col text-left">
                    <h4 className="font-black text-slate-800 text-sm pb-2 border-b border-slate-100 flex items-center gap-2">
                        <span className="w-1.5 h-4 bg-indigo-500 rounded-full"></span>
                        강사 프로필 및 경력 설정
                    </h4>

                    {/* Profile Image Picker */}
                    <div className="flex flex-col items-center gap-3 py-2 bg-slate-50/50 rounded-3xl border border-slate-100 p-4">
                        <span className="text-[10px] font-black text-slate-455 uppercase tracking-widest">프로필 사진</span>
                        <div 
                            className={`relative w-32 h-40 rounded-2xl overflow-hidden flex flex-col items-center justify-center shadow-sm cursor-pointer transition-all duration-250 border-2 ${
                                isDraggingProfile 
                                    ? 'border-dashed border-indigo-500 bg-indigo-50/50 scale-102 ring-4 ring-indigo-500/10' 
                                    : 'border-slate-200 bg-slate-50 hover:border-indigo-400'
                            }`}
                            onDragOver={handleProfileDragOver}
                            onDragLeave={handleProfileDragLeave}
                            onDrop={handleProfileDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {imagePreview ? (
                                <div className="relative w-full h-full group">
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover pointer-events-none" />
                                    <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                                        <span className="text-[10px] font-black text-white bg-slate-900/60 px-2.5 py-1.5 rounded-lg">사진 변경</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2 p-4 text-center">
                                    <ImageIcon className={isDraggingProfile ? 'text-indigo-500 animate-bounce' : 'text-slate-350'} size={24} />
                                    <span className="text-[9px] font-black text-slate-400 break-keep">드래그하거나 클릭하여 파일 선택</span>
                                </div>
                            )}
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageChange}
                            className="hidden"
                            accept="image/*"
                        />
                    </div>

                    {/* Photo Visibility Toggles */}
                    <div className="w-full bg-slate-50 rounded-2xl p-4 border border-slate-150 space-y-2.5 text-left">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">사진 노출 설정 (가리기)</span>
                        <label className="flex items-center justify-between cursor-pointer group select-none">
                            <span className="text-xs font-bold text-slate-700 group-hover:text-indigo-600 transition-colors flex items-center gap-1.5">
                                {hideMainPhoto ? <EyeOff size={14} className="text-amber-500" /> : <Eye size={14} className="text-slate-400" />}
                                메인 화면(홈)에서 사진 가리기
                            </span>
                            <input
                                type="checkbox"
                                checked={hideMainPhoto}
                                onChange={(e) => setHideMainPhoto(e.target.checked)}
                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                            />
                        </label>
                        <label className="flex items-center justify-between cursor-pointer group select-none">
                            <span className="text-xs font-bold text-slate-700 group-hover:text-indigo-600 transition-colors flex items-center gap-1.5">
                                {hideIntroPhoto ? <EyeOff size={14} className="text-amber-500" /> : <Eye size={14} className="text-slate-400" />}
                                강사소개 페이지에서 사진 가리기
                            </span>
                            <input
                                type="checkbox"
                                checked={hideIntroPhoto}
                                onChange={(e) => setHideIntroPhoto(e.target.checked)}
                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                            />
                        </label>
                    </div>

                    {/* Name and Subject Inputs in Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-0.5">
                                강사 이름
                            </label>
                            <input
                                type="text"
                                required
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm font-semibold bg-white text-slate-800"
                                placeholder="성함을 입력하세요."
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={saving}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-0.5">
                                담당 과목
                            </label>
                            <select
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm font-semibold bg-white text-slate-800"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value as SubjectType)}
                                disabled={saving}
                            >
                                {SUBJECT_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* YouTube Video URL */}
                    <div className="space-y-2">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-0.5 flex items-center gap-1">
                            <Youtube size={14} className="text-red-500" />
                            유튜브 OT 영상 링크 (선택)
                        </label>
                        <input
                            type="text"
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm font-semibold bg-white text-slate-800"
                            placeholder="예시: https://www.youtube.com/watch?v=... 또는 https://youtu.be/..."
                            value={youtubeUrl}
                            onChange={(e) => setYoutubeUrl(e.target.value)}
                            disabled={saving}
                        />
                    </div>

                    {/* Career Input */}
                    <div className="space-y-2">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-0.5 flex items-center gap-1">
                            <Award size={14} className="text-indigo-500" />
                            주요 경력 (줄바꿈 가능)
                        </label>
                        <textarea
                            rows={4}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm font-medium resize-none leading-relaxed bg-white text-slate-800"
                            placeholder="예시:&#10;- 현) 정성우학원 수학 대표 강사&#10;- 전) OO학원 대표 강사"
                            value={career}
                            onChange={(e) => setCareer(e.target.value)}
                            disabled={saving}
                        />
                    </div>

                    {/* Intro Input */}
                    <div className="space-y-2">
                        <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-0.5">
                            강사 소개글
                        </label>
                        <textarea
                            rows={4}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm font-medium resize-y leading-relaxed bg-white text-slate-800"
                            placeholder="강사진 소개 영역에 노출될 강사의 대표 소개글 또는 다짐을 입력해 주세요."
                            value={intro}
                            onChange={(e) => setIntro(e.target.value)}
                            disabled={saving}
                        />
                    </div>
                </div>

                {/* Right Panel: Textbook Management */}
                <div className="lg:col-span-7 p-8 overflow-y-auto space-y-6 bg-slate-50/50 flex flex-col border-t lg:border-t-0 lg:border-l border-slate-100 text-left">
                    <h4 className="font-black text-slate-800 text-sm pb-2 border-b border-slate-100 flex items-center gap-2">
                        <span className="w-1.5 h-4 bg-indigo-500 rounded-full"></span>
                        대표 교재 목록 관리 ({booksList.length})
                    </h4>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
                        {/* Current Books Grid */}
                        <div className="space-y-3">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block pl-0.5">
                                등록된 교재 목록
                            </span>
                            {booksList.length > 0 ? (
                                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                                    {booksList.map((book, index) => (
                                        <div key={book.id} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-3xl shadow-sm hover:shadow transition-shadow">
                                            <div className="flex gap-4 items-center min-w-0 flex-1">
                                                {book.imageUrl && (
                                                    <div className="w-11 h-16 rounded bg-slate-950 border border-slate-200 overflow-hidden shrink-0 shadow-sm flex items-center justify-center">
                                                        <img src={book.imageUrl} alt={book.name} className="w-full h-full object-cover" />
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="font-black text-slate-800 text-sm truncate">{book.name}</p>
                                                    {book.desc && (
                                                        <p className="text-[11px] text-slate-400 mt-1 leading-normal line-clamp-2 whitespace-pre-wrap">{book.desc}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleStartEditBook(index)}
                                                    className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-600 flex items-center justify-center shrink-0 transition-colors border border-slate-100 cursor-pointer"
                                                    title="교재 수정"
                                                >
                                                    <Edit2 size={15} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveBookFromList(index)}
                                                    className="w-9 h-9 rounded-xl bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 flex items-center justify-center shrink-0 transition-colors border border-red-100 cursor-pointer"
                                                    title="교재 삭제"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="w-full py-16 text-center rounded-3xl border-2 border-dashed border-slate-200 bg-white text-slate-400 text-xs font-bold">
                                    <BookOpen className="mx-auto text-slate-200 mb-3" size={32} />
                                    등록된 대표 교재 정보가 없습니다.
                                </div>
                            )}
                        </div>

                        {/* Add/Edit New Book Form Panel */}
                        <div className="p-6 bg-white border border-slate-100 rounded-[2rem] space-y-4 shadow-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-black text-indigo-650 uppercase tracking-wider block">
                                    {editingBookIndex !== null ? '✏️ 교재 수정 중' : '+ 새 교재 라인업 추가하기'}
                                </span>
                                {editingBookIndex !== null && (
                                    <button onClick={handleCancelEditBook} className="text-[10px] font-black text-slate-400 hover:text-red-500 underline">취소</button>
                                )}
                            </div>

                            {/* Book Image Upload Block */}
                            <div className="flex flex-col items-center gap-3">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5 self-start">
                                    교재 표지 이미지
                                </span>
                                <div 
                                    className={`relative w-28 h-36 rounded-2xl overflow-hidden flex items-center justify-center shadow-sm shrink-0 border-2 transition-all cursor-pointer w-full bg-slate-50 ${
                                        isDraggingBook 
                                            ? 'border-dashed border-indigo-500 bg-indigo-50/50 scale-102 ring-4 ring-indigo-500/10' 
                                            : 'border-slate-200 hover:border-indigo-400'
                                    }`}
                                    onDragOver={handleBookDragOver}
                                    onDragLeave={handleBookDragLeave}
                                    onDrop={handleBookDrop}
                                    onClick={() => bookFileInputRef.current?.click()}
                                >
                                    {newBookImagePreview ? (
                                        <div className="relative w-full h-full group">
                                            <img src={newBookImagePreview} alt="Preview" className="w-full h-full object-cover pointer-events-none" />
                                            <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                                                <span className="text-[10px] font-black text-white bg-slate-900/60 px-2 py-1 rounded-lg">교재 이미지 변경</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-2 p-4 text-center">
                                            <BookOpen className={isDraggingBook ? 'text-indigo-500 animate-bounce' : 'text-indigo-400/40'} size={24} />
                                            <span className="text-[9px] font-black text-slate-400 break-keep">커버 이미지를 드래그하거나 클릭</span>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={bookFileInputRef}
                                    onChange={handleBookImageChange}
                                    className="hidden"
                                    accept="image/*"
                                    disabled={uploadingBookFile}
                                />
                            </div>

                            {/* Book Name */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5">교재명</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-xs font-semibold bg-white text-slate-800"
                                    placeholder="교재 이름을 입력하세요."
                                    value={newBookName}
                                    onChange={(e) => setNewBookName(e.target.value)}
                                    disabled={uploadingBookFile}
                                />
                            </div>

                            {/* Book Desc */}
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5">교재 상세 설명 (선택)</label>
                                <textarea
                                    rows={2}
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-xs font-semibold bg-white text-slate-800 resize-none"
                                    placeholder="교재의 특징이나 활용 방안을 짧게 설명해 주세요."
                                    value={newBookDesc}
                                    onChange={(e) => setNewBookDesc(e.target.value)}
                                    disabled={uploadingBookFile}
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleSaveBook}
                                disabled={uploadingBookFile || !newBookName.trim() || (editingBookIndex === null && !newBookImageFile)}
                                className="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-650 py-3 rounded-xl font-black text-xs flex items-center justify-center gap-1.5 transition-all shadow-sm border border-indigo-100 cursor-pointer disabled:opacity-40"
                            >
                                {uploadingBookFile ? (
                                    <>
                                        <Loader2 className="animate-spin" size={14} />
                                        업로드 중...
                                    </>
                                ) : (
                                    <>
                                        <Check size={14} />
                                        {editingBookIndex !== null ? '교재 수정 완료' : '라인업에 교재 추가'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer sticky bar */}
            <div className="bg-white border-t border-slate-200 px-8 py-4 flex justify-end gap-3 shrink-0 shadow-lg">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="px-6 py-3 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-xl font-black text-sm transition-all active:scale-97 cursor-pointer"
                >
                    취소
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || uploadingBookFile}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-100 disabled:opacity-50 active:scale-97 cursor-pointer"
                >
                    {saving ? (
                        <>
                            <Loader2 className="animate-spin" size={16} />
                            저장 중...
                        </>
                    ) : (
                        <>
                            <Check size={16} />
                            저장하기
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default TeacherForm;
